import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CardComp, SelectComp2 } from "../../../components";
import PageWrapper from "../modules/PageWrapper";
import SearchBar from "../modules/SearchBar";
import TableSkeletonLoader from "../../../components/Common/TableSkeletonLoader";
import { TableCompData } from "../../../components";
import EmptyTable from "../../../components/Common/EmptyTable";
import TablePagination from "../../../components/Table/Pagination";
import {
  useGetMemberMessagesQuery,
} from "../../../store/member/member-communication";
import { MemberCommunicationMessagesHeader } from "../../../utils/TableHeader";
import styled from "styled-components";
import { AnimatePresence } from "framer-motion";
import { PageTab } from "../../../components";

const Communication = () => {
  const navigate = useNavigate();
  const [pageNumber, setPageNumber] = useState(1);
  const [tabName, setTab] = useState("All Messages");
  const [channel, setChannel] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");

  // Determine is_read filter based on active tab
  const isRead = tabName === "Unread Messages" ? "false" : "all";

  const {
    data = {},
    isLoading,
    isFetching,
    refetch,
  } = useGetMemberMessagesQuery({
    page: pageNumber,
    limit: 20,
    channel: channel,
    from_date: fromDate || undefined,
    to_date: toDate || undefined,
    is_read: isRead,
    search: search || undefined,
  });

  const { communication_group_messages = [], messages = [], pagination = {} } = data;
  // Handle both possible response formats
  const messageList = communication_group_messages.length > 0 ? communication_group_messages : messages;

  const handleViewDetail = (row) => {
    if (row?.id) navigate(`/member/communication/messages/${row.id}`);
  };

  const handleTabChange = (tab) => {
    setTab(tab);
    setPageNumber(1); // Reset to first page when switching tabs
    // Refetch data when switching tabs to ensure fresh data
    setTimeout(() => refetch(), 100);
  };

  const channelOptions = [
    { label: "All Channels", value: "all" },
    { label: "SMS", value: "sms" },
    { label: "Email", value: "email" },
    { label: "Both", value: "both" },
  ];

  const tabs = [
    {
      name: "All Messages",
      component: (
        <div>
          <CardComp cardClassName="border-0 shadow-sm mb-4">
            <div className="row g-3 p-3 align-items-end">
              <div className="col-lg-4 col-md-6">
                <div className="communication-search-wrapper">
                  <SearchBar
                    placeholder="Search by title, content..."
                    value={search}
                    onSearch={(newValue) => {
                      setSearch(newValue);
                      setPageNumber(1);
                    }}
                  />
                </div>
              </div>
              <div className="col-lg-2 col-md-6">
                <SelectComp2
                  setSelectedOption={(option) => {
                    setChannel(option?.value || "all");
                    setPageNumber(1);
                  }}
                  selectedOption={channel}
                  LabelText={"Channel"}
                  LabelClassName={"fw-2"}
                  selectClassName={"w-100 py-2"}
                  arrayComp={channelOptions}
                />
              </div>
              <div className="col-lg-3 col-md-6">
                <label className="form-label fw-2">From Date</label>
                <input
                  type="date"
                  className="form-control py-2"
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    setPageNumber(1);
                  }}
                />
              </div>
              <div className="col-lg-3 col-md-6">
                <label className="form-label fw-2">To Date</label>
                <input
                  type="date"
                  className="form-control py-2"
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setPageNumber(1);
                  }}
                />
              </div>
            </div>
          </CardComp>

          <CardComp cardClassName="border-0 shadow-sm" data-tour="member-msg-open">
            <TableCompData
              marginBottom={"2rem"}
              columns={MemberCommunicationMessagesHeader(handleViewDetail)}
              data={messageList.map((item, index) => ({
                ...item,
                serialNumber:
                  index + 1 + (pageNumber - 1) * (pagination.perPage || 20),
              }))}
              progressPending={isFetching || isLoading}
              progressComponent={<TableSkeletonLoader rows={10} columns={9} />}
              message={
                !isLoading &&
                !isFetching &&
                messageList.length === 0 ? (
                  <EmptyTable Message={"No messages found"} />
                ) : null
              }
            />
            {messageList.length > 0 && (
              <TablePagination
                handlePageChange={setPageNumber}
                {...pagination}
              />
            )}
          </CardComp>
        </div>
      ),
    },
    {
      name: "Unread Messages",
      component: (
        <div>
          <CardComp cardClassName="border-0 shadow-sm mb-4">
            <div className="row g-3 p-3 align-items-end">
              <div className="col-lg-4 col-md-6">
                <div className="communication-search-wrapper">
                  <SearchBar
                    placeholder="Search by title, content..."
                    value={search}
                    onSearch={(newValue) => {
                      setSearch(newValue);
                      setPageNumber(1);
                    }}
                  />
                </div>
              </div>
              <div className="col-lg-2 col-md-6">
                <SelectComp2
                  setSelectedOption={(option) => {
                    setChannel(option?.value || "all");
                    setPageNumber(1);
                  }}
                  selectedOption={channel}
                  LabelText={"Channel"}
                  LabelClassName={"fw-2"}
                  selectClassName={"w-100 py-2"}
                  arrayComp={channelOptions}
                />
              </div>
              <div className="col-lg-3 col-md-6">
                <label className="form-label fw-2">From Date</label>
                <input
                  type="date"
                  className="form-control py-2"
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    setPageNumber(1);
                  }}
                />
              </div>
              <div className="col-lg-3 col-md-6">
                <label className="form-label fw-2">To Date</label>
                <input
                  type="date"
                  className="form-control py-2"
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setPageNumber(1);
                  }}
                />
              </div>
            </div>
          </CardComp>

          <CardComp cardClassName="border-0 shadow-sm" data-tour="member-msg-open">
            <TableCompData
              marginBottom={"2rem"}
              columns={MemberCommunicationMessagesHeader(handleViewDetail)}
              data={messageList.map((item, index) => ({
                ...item,
                serialNumber:
                  index + 1 + (pageNumber - 1) * (pagination.perPage || 20),
              }))}
              progressPending={isFetching || isLoading}
              progressComponent={<TableSkeletonLoader rows={10} columns={9} />}
              message={
                !isLoading &&
                !isFetching &&
                messageList.length === 0 ? (
                  <EmptyTable Message={"No unread messages found"} />
                ) : null
              }
            />
            {messageList.length > 0 && (
              <TablePagination
                handlePageChange={setPageNumber}
                {...pagination}
              />
            )}
          </CardComp>
        </div>
      ),
    },
  ];

  return (
    <Wrapper>
      <PageWrapper title={"Messages"}>
        <div className="tab-header" data-tour="member-msg-inbox">
          {tabs.map(({ name }) => {
            return (
              <p
                key={name}
                className={`tab-name ${
                  tabName === name ? "active-tab" : ""
                } mb-0`}
                onClick={() => handleTabChange(name)}
              >
                {name}
              </p>
            );
          })}
        </div>
        <AnimatePresence exitBeforeEnter>
          <PageTab page={tabName} PageArray={tabs} />
        </AnimatePresence>
      </PageWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.section`
  .tab-header {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    padding: 2rem 2rem 1rem 2rem;
    background-color: #ffffff;
    border-radius: 10px;
    margin-bottom: 1rem;
  }
  .tab-name {
    padding: 0 1rem;
    color: #858585;
    font-weight: 400;
    cursor: pointer;
    transition: all 300ms linear;
  }
  .active-tab {
    color: var(--color-primary);
    border-bottom: 2px solid var(--color-primary);
    font-weight: 700;
  }
  .communication-search-wrapper .input-group.search {
    margin-bottom: 0 !important;
  }
`;

export default Communication;
